<template>
    <div class="max-w-(--breakpoint-md) flex flex-col gap-4">
        <page-header>Amplitude</page-header>

        <vx-expandable-card>
            <template v-slot:header>Amplitude Integration</template>
            <template v-slot:headerRight>
                <div v-if="connectionStatus === 'loading'">
                    <vx-badge color="slate">Loading</vx-badge>
                </div>
                <div v-else-if="connectionStatus === 'connected'">
                    <vx-badge color="success">Connected</vx-badge>
                </div>
                <div v-else>
                    <vx-badge color="slate">Not connected</vx-badge>
                </div>
            </template>

            <template v-slot:content>
                <div v-if="!props.ownsTeam" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner can configure integrations.
                </div>
                <div v-else-if="connectionStatus === 'connected'" class="flex flex-col gap-6 mt-3">
                    <div>
                        To regenerate your credentials, click the button below. You will need to update them in your
                        <a
                            href="https://app.amplitude.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-600 hover:underline"
                            data-test="amplitude-link"
                            >Amplitude Data Destinations</a
                        >
                        configuration.
                    </div>

                    <div class="flex gap-3">
                        <vx-button
                            color="primary"
                            size="lg"
                            @click="regenerateCredentials"
                            :loading="generating"
                            data-test="regenerate-button"
                        >
                            Regenerate Credentials
                        </vx-button>

                        <vx-button
                            color="danger"
                            size="lg"
                            @click="disconnect"
                            :loading="disconnecting"
                            data-test="disconnect-button"
                        >
                            Disconnect
                        </vx-button>
                    </div>
                </div>
                <div v-else-if="connectionStatus === 'not_connected'" class="flex flex-col gap-6 mt-3">
                    <div>
                        Click the button below to generate your Amplitude credentials. You will need to paste them into
                        your
                        <a
                            href="https://app.amplitude.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-600 hover:underline"
                            data-test="amplitude-link"
                            >Amplitude Data Destinations</a
                        >
                        configuration.
                    </div>

                    <vx-button size="lg" @click="connect" :loading="generating" data-test="connect-button">
                        Connect
                    </vx-button>
                </div>
            </template>
        </vx-expandable-card>

        <amplitude-documentation />

        <!-- Credentials Modal -->
        <vx-modal
            v-model:visible="showCredentialsModal"
            preventClosing
            padding="standard"
            size="lg"
            data-test="credentials-modal"
        >
            <template v-slot:header>Amplitude Credentials</template>

            <vx-alert color="info" :closeable="false">
                Your credentials have been generated. Please copy them now and paste them into your Amplitude Data
                Destinations configuration. <strong>These values will only be shown once.</strong>
            </vx-alert>

            <div class="space-y-3 mt-4">
                <div>
                    <div class="text-sm font-medium mb-2">Key</div>
                    <vx-popover hover arrow placement="top">
                        <div
                            @click="copyValue(credentials?.key, 'Key')"
                            class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm"
                            data-test="copy-key-button"
                        >
                            {{ credentials?.key }} <font-awesome-icon widthAuto :icon="faCopy" />
                        </div>
                        <template v-slot:content>Click to Copy</template>
                    </vx-popover>
                </div>

                <div>
                    <div class="text-sm font-medium mb-2">Secret</div>
                    <vx-popover hover arrow placement="top">
                        <div
                            @click="copyValue(credentials?.secret, 'Secret')"
                            class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm"
                            data-test="copy-secret-button"
                        >
                            {{ credentials?.secret }} <font-awesome-icon widthAuto :icon="faCopy" />
                        </div>
                        <template v-slot:content>Click to Copy</template>
                    </vx-popover>
                </div>
            </div>

            <template v-slot:footer>
                <vx-button
                    block
                    color="primary"
                    size="lg"
                    @click="closeCredentialsModal"
                    data-test="close-modal-button"
                >
                    I've copied the credentials
                </vx-button>
            </template>
        </vx-modal>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { VxExpandableCard, VxButton, VxBadge, VxAlert, VxModal, VxPopover, VxSwal } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import PageHeader from '~/components/general/PageHeader.vue';
import AmplitudeDocumentation from './amplitude/AmplitudeDocumentation.vue';
import teamsClient from '~/services/teamsClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

type Credentials = {
    key: string;
    secret: string;
};

const props = defineProps<{
    ownsTeam: boolean;
}>();

const toasted = useToasted();
const connectionStatus = ref<'loading' | 'connected' | 'not_connected'>('loading');
const credentials = ref<Credentials | null>(null);
const showCredentialsModal = ref(false);
const generating = ref(false);
const disconnecting = ref(false);

onMounted(async () => {
    try {
        const response = await teamsClient.getAmplitudeStatus(Spark.state.currentTeam.id);
        connectionStatus.value = response.data.status;
    } catch (error) {
        console.error(error);
        connectionStatus.value = 'not_connected';
    }
});

const connect = async () => {
    generating.value = true;
    try {
        const response = await teamsClient.generateAmplitudeCredentials(Spark.state.currentTeam.id);
        credentials.value = response.data;
        connectionStatus.value = 'connected';
        showCredentialsModal.value = true;
        toasted.success('Amplitude credentials generated successfully.');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        generating.value = false;
    }
};

const regenerateCredentials = () => {
    VxSwal.confirmation(
        'Regenerate Credentials',
        'Are you sure you want to regenerate your Amplitude credentials? You will need to update them in your Amplitude Data Destinations configuration, otherwise your integration will stop working.',
        'warning',
        async () => {
            generating.value = true;
            try {
                const response = await teamsClient.generateAmplitudeCredentials(Spark.state.currentTeam.id);
                credentials.value = response.data;
                showCredentialsModal.value = true;
                toasted.success('Amplitude credentials regenerated successfully. Please update them in Amplitude.');
            } catch (error) {
                toasted.error(catchRequestError(error));
            } finally {
                generating.value = false;
            }
        }
    );
};

const disconnect = () => {
    VxSwal.confirmation(
        'Disconnect Amplitude',
        'Are you sure you want to disconnect your Amplitude integration? This action cannot be undone.',
        'danger',
        async () => {
            disconnecting.value = true;
            try {
                await teamsClient.disconnectAmplitude(Spark.state.currentTeam.id);
                connectionStatus.value = 'not_connected';
                toasted.success('Amplitude integration disconnected successfully.');
            } catch (error) {
                toasted.error(catchRequestError(error));
            } finally {
                disconnecting.value = false;
            }
        }
    );
};

const copyValue = (value: string | undefined, label: string) => {
    if (!value) {
        return;
    }
    navigator.clipboard
        .writeText(value)
        .then(() => {
            toasted.success(`${label} copied to clipboard.`);
        })
        .catch((error) => {
            console.error(error);
            toasted.error(`Failed to copy ${label}.`);
        });
};

const closeCredentialsModal = () => {
    showCredentialsModal.value = false;
    credentials.value = null;
};
</script>
